/**
 * use-dsar.ts — "Yêu cầu dữ liệu cá nhân" (Cài đặt → Pháp lý & Dữ liệu cá nhân).
 *
 * Luật Bảo vệ dữ liệu cá nhân 91/2025/QH15 + NĐ 356/2025/NĐ-CP. Backend: dsar-routes.ts.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api-client'
import { i18n } from '@/lib/i18n'
import { useApiQuery } from '@/hooks/use-api'

export type DsarType = 'access' | 'rectification' | 'erasure' | 'restriction' | 'withdraw_consent'
export type DsarStatus = 'pending' | 'in_progress' | 'done' | 'rejected'

export const DSAR_TYPE_LABEL: Record<DsarType, string> = {
  get access() { return i18n.t('sharedMisc:dsar.type.access') },
  get rectification() { return i18n.t('sharedMisc:dsar.type.rectification') },
  get erasure() { return i18n.t('sharedMisc:dsar.type.erasure') },
  get restriction() { return i18n.t('sharedMisc:dsar.type.restriction') },
  get withdraw_consent() { return i18n.t('sharedMisc:dsar.type.withdrawConsent') },
}

export const DSAR_STATUS_LABEL: Record<DsarStatus, string> = {
  get pending() { return i18n.t('sharedMisc:dsar.status.pending') },
  get in_progress() { return i18n.t('sharedMisc:dsar.status.inProgress') },
  get done() { return i18n.t('sharedMisc:dsar.status.done') },
  get rejected() { return i18n.t('sharedMisc:dsar.status.rejected') },
}

export interface DsarContactRef {
  id: string
  fullName?: string | null
  crmName?: string | null
  phone?: string | null
  email?: string | null
  deleted?: boolean
}

export interface DsarRequest {
  id: string
  orgId: string
  contactId: string
  type: DsarType
  receivedAt: string
  dueAt: string
  status: DsarStatus
  handledByUserId: string | null
  handledByUserName?: string | null
  note: string | null
  createdAt: string
  updatedAt: string
  contact: DsarContactRef
}

export interface DsarRequestDetail extends DsarRequest {
  erasureConfirmPhrase: string | null
}

export interface DsarListResponse {
  items: DsarRequest[]
  total: number
  page: number
  limit: number
}

export interface DsarFilters {
  status?: string
  type?: string
  overdue?: boolean
  page?: number
  limit?: number
}

export function useDsarRequests(filters: DsarFilters) {
  const params: Record<string, unknown> = { page: filters.page ?? 1, limit: filters.limit ?? 20 }
  if (filters.status) params.status = filters.status
  if (filters.type) params.type = filters.type
  if (filters.overdue) params.overdue = 'true'
  return useApiQuery<DsarListResponse>(['dsar-requests', filters], '/dsar-requests', params, { placeholderData: (prev) => prev })
}

export function useDsarRequest(id: string | undefined) {
  return useQuery<DsarRequestDetail>({
    queryKey: ['dsar-request', id],
    queryFn: async () => (await api.get<DsarRequestDetail>(`/dsar-requests/${id}`)).data,
    enabled: !!id,
  })
}

export function useCreateDsarRequest() {
  const qc = useQueryClient()
  return useMutation<DsarRequest, unknown, { contactId: string; type: DsarType; note?: string; receivedAt?: string }>({
    mutationFn: async (body) => (await api.post<DsarRequest>('/dsar-requests', body)).data,
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['dsar-requests'] }) },
  })
}

export function useUpdateDsarRequest() {
  const qc = useQueryClient()
  return useMutation<DsarRequest, unknown, { id: string; status?: DsarStatus; note?: string }>({
    mutationFn: async ({ id, ...body }) => (await api.patch<DsarRequest>(`/dsar-requests/${id}`, body)).data,
    onSuccess: (_d, vars) => {
      void qc.invalidateQueries({ queryKey: ['dsar-requests'] })
      void qc.invalidateQueries({ queryKey: ['dsar-request', vars.id] })
    },
  })
}

/** Tên tệp JSON xuất — dsar-<id ngắn>-YYYYMMDD-HHmm.json. Hàm thuần. */
export function dsarExportFileName(requestId: string, now = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  const stamp = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}-${pad(now.getHours())}${pad(now.getMinutes())}`
  return `du-lieu-ca-nhan-${requestId.slice(0, 8)}-${stamp}.json`
}

/** Xuất JSON đầy đủ dữ liệu 1 khách (yêu cầu loại access) rồi tải về máy. */
export async function downloadDsarExport(requestId: string): Promise<void> {
  const { data } = await api.get(`/dsar-requests/${requestId}/export`)
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = dsarExportFileName(requestId)
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

export interface EraseResult {
  contactId: string
  deletedConversations: number
  deletedMessages: number
  deletedFiles: number
  failedFileDeletes: number
}

/** XOÁ VĨNH VIỄN — KHÔNG THỂ HOÀN TÁC. `confirmText` phải khớp đúng `erasureConfirmPhrase` của yêu cầu. */
export function useEraseDsarRequest() {
  const qc = useQueryClient()
  return useMutation<EraseResult, unknown, { id: string; confirmText: string }>({
    mutationFn: async ({ id, confirmText }) => (await api.post<EraseResult>(`/dsar-requests/${id}/erase`, { confirmText })).data,
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['dsar-requests'] }) },
  })
}
